import BaseLayout from '@/layout/BaseLayout';
import { RestrictedComponent } from '@/layout/RestrictedComponent';
import { Button } from '@shelflix/ui-web/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@shelflix/ui-web/components/ui/card';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  scrollToError,
} from '@shelflix/ui-web/components/ui/form';
import { Input } from '@shelflix/ui-web/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@shelflix/ui-web/components/ui/select';
import { useForm } from 'react-hook-form';

import { GenericSelect } from '@/components/GenericSelect';
import { UserDeletionModal } from '@/components/UserDeletionModal';
import { trpc, trpcClient } from '@/trpcClient';
import { checkUserEditInput, type EditUserInputType } from '@shelflix/schemas/user/edit';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useCallback, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import type { UserDto } from '@shelflix/trpc/routes/user/list/useCase';
import Icon from '@shelflix/ui-web/components/Icon';
import { RadioGroup, RadioGroupItem } from '@shelflix/ui-web/components/ui/radio-group';
import { toast } from 'sonner';
import { useUserFormStore } from './store';

export function UserEdit() {
  const { uuid: userId } = useParams<{ uuid: string }>();

  const { data: userToEdit, isLoading: _isLoadingUser } = useQuery(
    trpc.user.get.queryOptions(
      {
        uuid: userId ?? '',
      },
      {
        enabled: !!userId,
      }
    )
  );

  const { formData, resetForm } = useUserFormStore();
  const editUserMutation = useMutation(trpc.user.edit.mutationOptions());
  const deleteUserMutation = useMutation(trpc.user.delete.mutationOptions());
  const navigate = useNavigate();

  // State for deletion modal
  const [deletionModalData, setDeletionModalData] = useState<{
    isOpen: boolean;
    canDelete: boolean;
    dependencies?: {
      hasSubordinates: boolean;
      subordinates: Array<{ uuid: string; name: string }>;
      hasFacilities: boolean;
      facilities: Array<{ uuid: string; name: string }>;
    };
  }>({
    isOpen: false,
    canDelete: true,
  });

  const handleDeleteUser = () => {
    if (!userId) return;

    deleteUserMutation.mutate(
      { uuid: userId },
      {
        onSuccess: (response) => {
          if (!response.canDelete && response.dependencies) {
            // User has dependencies - show warning modal (no confirm button)
            setDeletionModalData({
              isOpen: true,
              canDelete: false,
              dependencies: response.dependencies,
            });
          } else if (response.success) {
            // User deleted successfully
            toast.success('Usuário excluído com sucesso');
            navigate('/user');
          } else {
            // User can be deleted - show confirmation modal
            setDeletionModalData({
              isOpen: true,
              canDelete: true,
            });
          }
        },
        onError: (error) => {
          toast.error(`Erro ao verificar dependências do usuário: ${error.message}`);
        },
      }
    );
  };

  const confirmDeletion = () => {
    if (!userId) return;

    deleteUserMutation.mutate(
      { uuid: userId },
      {
        onSuccess: (response) => {
          if (response.success) {
            toast.success('Usuário excluído com sucesso');
            navigate('/user');
          }
        },
        onError: (error) => {
          toast.error(`Erro ao excluir usuário: ${error.message}`);
          setDeletionModalData({ isOpen: false, canDelete: true });
        },
      }
    );
  };

  const form = useForm<
    EditUserInputType & {
      responsibleUser: {
        uuid: string;
        name: string;
      };
    }
  >({
    defaultValues: {
      name: formData?.name ?? '',
      email: formData?.email ?? '',
      phone: formData?.phone ?? '',
      registration: formData?.registration ?? '',
      role: formData?.role ?? '',
      responsibleUser: formData?.responsibleUser ?? null,
      active: formData?.active ?? true,
    },
    mode: 'onSubmit',
  });

  useEffect(() => {
    if (userToEdit) {
      form.reset({
        name: userToEdit.name,
        email: userToEdit.email,
        phone: userToEdit.phone,
        registration: userToEdit.registration ?? undefined,
        role: userToEdit.role.uuid,
        responsibleUser: userToEdit.responsibleUser ?? undefined,
        active: userToEdit.active,
      });
      form.trigger();
    }
  }, [userToEdit]);

  const { data: roles = [] } = useQuery(
    trpc.role.list.queryOptions({ sort: { field: 'hierarchy', direction: 'asc' } })
  );

  const onSubmit = (
    data: EditUserInputType & {
      active?: boolean;
      responsibleUser: {
        uuid: string;
        name: string;
      };
    }
  ) => {
    if (editUserMutation.isPending) return;
    const payload = {
      email: data.email,
      name: data.name,
      phone: data.phone.replaceAll(/[^\d]/g, ''),
      registration: data.registration ?? undefined,
      responsibleUser:
        data.role !== roles.find((r) => r.hierarchy === Math.min(...roles.map((r) => r.hierarchy)))?.uuid
          ? (data.responsibleUser.uuid ?? data.responsibleUser)
          : '00000000-0000-4000-8000-000000000000', //FIXME setting valid uuid for admin to avoid validation error

      role: data.role,
      active: !!data.active,
    };

    if (userId) {
      editUserMutation.mutate(
        { uuid: userId, ...payload, role: payload.role },
        {
          onSuccess: () => {
            navigate('/user', { state: { toastSuccess: true } });
            resetForm();
          },
          onError: (error) => {
            toast.error(`Erro ao editar usuário: ${error.message}`);
          },
        }
      );
    }
  };

  const currentRole = form.watch('role');

  const fetchUsers = useCallback(
    async (searchValue: string) => {
      if (!searchValue || !currentRole) return [];

      // Fetch directly via trpcClient to avoid stale data from useQuery cache
      const result = await trpcClient.user.list.query({
        search: {
          fields: ['name', 'email'],
          value: searchValue,
        },
        filter: [
          { name: 'active', condition: 'equals', value: true },
          { name: 'hierarchy', condition: 'lt', value: roles.find((r) => r.uuid === currentRole)?.hierarchy ?? 0 },
        ],
      });

      return result.users ?? [];
    },
    [currentRole, roles]
  );

  return (
    <BaseLayout>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit, scrollToError)} className="flex w-full flex-col gap-4">
          <Card className="rounded-[10px]">
            <CardHeader className="pb-0">
              <CardTitle className="text-primary text-[18px] leading-5 font-semibold sm:text-2xl">
                Informações pessoais
              </CardTitle>
            </CardHeader>
            <CardContent className="mt-3 grid gap-2 sm:grid-cols-2 sm:gap-[27px_12px] xl:grid-cols-4">
              <FormField
                control={form.control}
                name="name"
                rules={{ validate: (value) => checkUserEditInput('name', value) }}
                render={({ field }) => (
                  <FormItem className="col-span-3 flex flex-col gap-[3px] sm:col-span-1">
                    <FormLabel className="text-xs font-medium text-[var(--gray-primary)]">Nome e sobrenome</FormLabel>
                    <FormControl>
                      <Input className="h-10" placeholder="Digite o nome do usuário" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="phone"
                rules={{ validate: (value) => checkUserEditInput('phone', value?.replaceAll(/[^\d]/g, '')) }}
                render={({ field }) => (
                  <FormItem className="col-span-3 flex flex-col gap-[3px] sm:col-span-1">
                    <FormLabel className="text-xs font-medium text-[var(--gray-primary)]">Número de WhatsApp</FormLabel>
                    <FormControl>
                      <Input className="h-10" mask="(99) 99999-9999" placeholder="(__) _____-____" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="email"
                rules={{ validate: (value) => checkUserEditInput('email', value) }}
                render={({ field }) => (
                  <FormItem className="col-span-3 flex flex-col gap-[3px] sm:col-span-1">
                    <FormLabel className="text-xs font-medium text-[var(--gray-primary)]">E-mail</FormLabel>
                    <FormControl>
                      <Input className="h-10" placeholder="Digite o e-mail corporativo" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="registration"
                rules={{ validate: (value) => checkUserEditInput('registration', value) }}
                render={({ field }) => (
                  <FormItem className="col-span-3 flex flex-col gap-[3px] sm:col-span-1">
                    <FormLabel className="text-xs font-medium text-[var(--gray-primary)]">Matrícula</FormLabel>
                    <FormControl>
                      <Input className="h-11" placeholder="Digite a matrícula (opcional)" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </CardContent>
          </Card>

          <Card className="rounded-[10px]">
            <CardHeader className="pb-0">
              <CardTitle className="text-primary text-[18px] leading-5 font-semibold sm:text-2xl">
                Controle de acesso e visualização
              </CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col gap-8 sm:flex-row">
              <FormField
                control={form.control}
                name="role"
                rules={{ validate: (value) => checkUserEditInput('role', value) }}
                render={({ field }) => (
                  <FormItem className="flex flex-col gap-[3px] sm:w-sm">
                    <FormLabel className="text-xs font-medium text-[var(--gray-primary)]">Tipo de acesso</FormLabel>
                    <FormControl>
                      <Select
                        onValueChange={(uuid) => {
                          const selected = roles.find((r) => r.uuid === uuid);
                          if (selected) {
                            field.onChange(selected.uuid);
                          }
                        }}
                        defaultValue={field.value ?? undefined}
                        value={field.value ?? undefined}
                        disabled={userToEdit?.isMaster}
                      >
                        <SelectTrigger className="h-10 w-full">
                          <SelectValue placeholder="Selecione o tipo de acesso" />
                        </SelectTrigger>
                        <SelectContent className=" ">
                          {roles.map((role) => (
                            <SelectItem key={role.uuid} value={role.uuid}>
                              {role.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </FormControl>
                    <FormMessage>{userToEdit?.isMaster && 'Usuário master!'}</FormMessage>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="active"
                render={({ field }) => (
                  <FormItem className="flex flex-col gap-2.5">
                    <FormLabel className="text-xs font-medium text-[var(--gray-primary)]">Status</FormLabel>
                    <FormControl>
                      <RadioGroup
                        onValueChange={(value) => field.onChange(value === 'true')}
                        defaultValue={String(field.value)}
                        className="flex gap-6"
                        value={String(field.value)}
                        disabled={userToEdit?.isMaster}
                      >
                        <FormItem className="flex items-center space-y-0 space-x-2">
                          <FormControl>
                            <RadioGroupItem value={'true'} id="ativo" />
                          </FormControl>
                          <FormLabel
                            htmlFor="ativo"
                            className={
                              field.value === true ? 'text-primary text-sm font-semibold' : 'text-sm text-[#6d6d6d]'
                            }
                          >
                            Ativo
                          </FormLabel>
                        </FormItem>
                        <FormItem className="flex items-center space-y-0 space-x-2">
                          <FormControl>
                            <RadioGroupItem value="false" id="inativo" />
                          </FormControl>
                          <FormLabel
                            htmlFor="inativo"
                            className={
                              field.value === false ? 'text-primary text-sm font-semibold' : 'text-sm text-[#6d6d6d]'
                            }
                          >
                            Inativo
                          </FormLabel>
                        </FormItem>
                      </RadioGroup>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </CardContent>
          </Card>

          {!userToEdit?.isMaster &&
            !!currentRole &&
            roles.find((r) => r.uuid === currentRole)?.hierarchy !== Math.min(...roles.map((r) => r.hierarchy)) && (
              <Card className="rounded-[5px]">
                <CardContent>
                  <div className="mb-6">
                    <CardTitle className="text-primary mb-2 [font-family:'Poppins'] text-[18px] leading-5 font-semibold sm:text-2xl">
                      Responsável direto
                    </CardTitle>
                    <p className="[font-family:'Figtree'] text-sm leading-5 text-[var(--gray-primary)]/50">
                      Todo usuário deverá ter um responsável direto, de cargo superior. <br />
                      Caso não haja um cargo acima deste usuário cadastrado, informe abaixo o e-mail do administrador.
                    </p>
                  </div>

                  <FormField
                    control={form.control}
                    name="responsibleUser"
                    rules={{ validate: (value) => checkUserEditInput('responsibleUser', value?.uuid ?? value) }}
                    render={({ field }) => (
                      <FormItem className="flex flex-col gap-[3px] sm:w-[391px]">
                        <FormLabel className="text-xs font-medium text-[var(--gray-primary)]">
                          Pesquisar usuário
                        </FormLabel>
                        <FormControl>
                          <div className="flex flex-row items-center gap-2">
                            <GenericSelect<UserDto>
                              disabled={!currentRole}
                              onSelect={(user) => field.onChange(user)}
                              placeholder="Digite o nome para buscar"
                              value={field.value}
                              fetchItems={fetchUsers}
                              renderItem={(user) => (
                                <div>
                                  <div className="text-sm font-medium">{user.name}</div>
                                  <div className="text-muted-foreground text-xs">{user.email}</div>
                                  <div className="text-muted-foreground text-xs">{user.role.name}</div>
                                </div>
                              )}
                              getItemLabel={(user) => user.name}
                              getItemKey={(user) => user.uuid}
                            />
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </CardContent>
              </Card>
            )}

          <div className="mb-10 flex grow-0 flex-col justify-center gap-4 sm:flex-row">
            {!userToEdit?.isMaster && (
              <RestrictedComponent requiredScopes={['admin:users:delete']} behavior="hide">
                <Button
                  variant="destructive"
                  onClick={handleDeleteUser}
                  loading={deleteUserMutation.isPending}
                  disabled={editUserMutation.isPending}
                  type="button"
                >
                  <Icon name="delete" color="#fff" />
                  <span>Excluir Usuário</span>
                </Button>
              </RestrictedComponent>
            )}

            <Button
              type="submit"
              loading={editUserMutation.isPending}
              disabled={deleteUserMutation.isPending}
              className="bg-primary hover:bg-primary flex grow cursor-pointer items-center gap-2.5 rounded-[5px] px-12 py-2.5 font-semibold text-white sm:w-auto sm:w-fit sm:grow-0"
            >
              <Icon name="person" color="#fff" />
              <span className="[font-family:'Figtree'] text-sm">Salvar usuário</span>
            </Button>
          </div>

          <UserDeletionModal
            isOpen={deletionModalData.isOpen}
            onClose={() => setDeletionModalData({ isOpen: false, canDelete: true })}
            onConfirm={confirmDeletion}
            canDelete={deletionModalData.canDelete}
            userName={userToEdit?.name}
            dependencies={deletionModalData.dependencies}
            isLoading={deleteUserMutation.isPending}
          />
        </form>
      </Form>
    </BaseLayout>
  );
}
